import { Button, Stack, Typography, useTheme } from "@mui/material";
import { CheckCircle2, MessageCircle } from "lucide-react";
import type { FC } from "react";
import { RealizationStepRow } from "web_myplann/Goals/Components/RealizationStepRow";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { EARLY_FREEDOM_READY_STEPS } from "web_myplann/Inspiration/constants";
import type { EarlyFreedomContext } from "web_myplann/Inspiration/Utils/earlyFreedomMath";

const R = Resources.Inspiration.EarlyFreedom;

type Props = {
	context: EarlyFreedomContext;
	onContact: () => void;
};

export const EarlyFreedomReadyBox: FC<Props> = ({ context, onContact }) => {
	const { t } = useResource();
	const theme = useTheme();

	const accent = theme.palette.realization.main;
	const isFull = context.coverageRatio >= 100;

	return (
		<Stack px={1.6} py={2} sx={{ background: theme.gradients.realization }}>
			<Stack
				direction="row"
				alignItems="center"
				gap={0.6}
				alignSelf="flex-start"
				px={1}
				py={0.4}
				mb={1.2}
				borderRadius={999}
				bgcolor={theme.hero.pillBg}
			>
				<CheckCircle2 size={12} color={accent} />
				<Typography variant="overline" color={theme.palette.grey[700]}>
					{t(R.readyKicker)}
				</Typography>
			</Stack>

			<Typography variant="h2">
				{isFull ? t(R.readyTitleFull) : t(R.readyTitleAlmost)}
			</Typography>
			<Typography
				variant="caption"
				color={theme.palette.grey[600]}
				mt={0.6}
				lineHeight={1.375}
			>
				{isFull ? t(R.readyDescFull) : t(R.readyDescAlmost)}
			</Typography>

			<Stack mt={2} gap={1.25}>
				{EARLY_FREEDOM_READY_STEPS.map((step) => (
					<RealizationStepRow
						key={t(step.title)}
						step={{
							icon: step.icon,
							title: t(step.title),
							subtitle: t(step.subtitle),
						}}
						accent={accent}
					/>
				))}
			</Stack>

			<Button
				variant="primary"
				color="success"
				startIcon={<MessageCircle size={theme.iconSize.sm} />}
				onClick={onContact}
				sx={{ mt: 2 }}
			>
				{t(R.contactAdvisor)}
			</Button>
		</Stack>
	);
};
